import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FadeIn from '../components/FadeIn';
import { Mail, Phone, MapPin, Calendar, Shield, Lock, ArrowRight } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import SEO from '../components/SEO';
import ScheduleModal from '../components/ScheduleModal';
import { sendLeadNotification } from '../utils/emailService';

const Contact = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
    });

    const [isScheduleOpen, setIsScheduleOpen] = useState(false);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            // 1. Save to Firestore
            await addDoc(collection(db, 'leads'), {
                ...formData,
                source: 'contact_form',
                status: 'new',
                date_created: serverTimestamp()
            });

            // 2. Send Notification Email
            await sendLeadNotification(formData);

            // Redirect to Thank You page
            navigate('/thank-you');

            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                message: ''
            });
        } catch (error) {
            console.error('Error adding document: ', error);
            alert('Something went wrong. Please try again later.');
        }
    };

    const handleScheduleClick = (e) => {
        e.preventDefault();
        setIsScheduleOpen(true);
    };



    return (
        <div className="bg-white min-h-screen">
            <SEO
                pageKey="contact"
                defaultTitle="Contact Us | Free BPO Assessment"
                defaultDescription="Get in touch for a free AI-powered BPO assessment. Contact our team in Manila or book a consultation today."
                defaultSchema={JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "mainEntity": {
                        "@type": "Organization",
                        "name": "BPO Consultants",
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": "Taguig",
                            "addressRegion": "Metro Manila",
                            "addressCountry": "PH"
                        },
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "email": "info@bpoconsultants.ph",
                            "contactType": "customer support"
                        }
                    }
                }, null, 4)}
            />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#20222D] overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <img src="https://images.unsplash.com/photo-1596524430615-b46475ddff6e?auto=format&fit=crop&q=80&w=1200" alt="Customer support" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Let's Build Your Team
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
                            Ready to reduce costs and scale up? Get your free expert assessment today.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20 pb-20">
                <FadeIn>
                    <div className="grid lg:grid-cols-2 gap-8 items-start">
                        {/* Contact Info & Booking */}
                        <div className="space-y-8">
                            {/* Booking Card */}
                            <div className="bg-[#A2CB52] rounded-3xl p-8 shadow-2xl transform hover:scale-[1.01] transition-transform">
                                <div className="flex items-center mb-6">
                                    <Calendar className="text-[#20222D] mr-4" size={32} />
                                    <h3 className="text-2xl font-bold text-[#20222D]">Skip the Email Tag</h3>
                                </div>
                                <p className="text-[#20222D] mb-8 font-medium">
                                    Book a direct 15-minute strategy call with our solutions architect. No sales pressure, just answers.
                                </p>
                                <button
                                    onClick={handleScheduleClick}
                                    className="block w-full bg-[#20222D] text-white text-center py-4 rounded-xl font-bold hover:bg-gray-800 transition-colors"
                                >
                                    Schedule a Call Now
                                </button>
                            </div>

                            {/* Standard Contact Info */}
                            <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                                <h3 className="text-xl font-bold text-[#20222D] mb-6">Contact Information</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start space-x-4 group">
                                        <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-[#ECF5DC] transition-colors">
                                            <Mail className="text-[#A2CB52]" size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-400">Email Us</p>
                                            <p className="text-lg font-medium text-[#20222D]">info@bpoconsultants.ph</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4 group">
                                        <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-[#ECF5DC] transition-colors">
                                            <Phone className="text-[#A2CB52]" size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-400">Call Us</p>
                                            <p className="text-lg font-medium text-[#20222D]">Contact for Details</p>
                                            <p className="text-xs text-gray-400">Mon-Fri 8am-5pm GMT+8</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4 group">
                                        <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-[#ECF5DC] transition-colors">
                                            <MapPin className="text-[#A2CB52]" size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-400">Headquarters</p>
                                            <p className="text-lg font-medium text-[#20222D]">Bonifacio Global City</p>
                                            <p className="text-sm text-gray-500">Taguig, Metro Manila, Philippines</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl border border-gray-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#ECF5DC] rounded-bl-full -mr-16 -mt-16 opacity-50"></div>

                            <h3 className="text-2xl font-bold text-[#20222D] mb-2 relative z-10">Get Expert Advice on Building Your Remote Team</h3>
                            <p className="text-gray-500 mb-8 relative z-10">Fill out the form below and we'll get back to you within 24 hours.</p>

                            <form onSubmit={handleFormSubmit} className="space-y-5 relative z-10">
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="john@company.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                                        <input
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="Company Ltd."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="+1 (555) 000-0000"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">How can we help?</label>
                                    <textarea
                                        rows="4"
                                        required
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all bg-gray-50 focus:bg-white resize-none"
                                        placeholder="Tell us about your staffing needs..."
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-[#20222D] text-white font-bold py-4 rounded-xl hover:bg-gray-800 transition-all transform hover:translate-y-[-2px] shadow-lg flex items-center justify-center group"
                                >
                                    Send Message
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                                </button>

                                <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-100 mt-6">
                                    <div className="flex items-center text-xs text-gray-400">
                                        <Shield size={14} className="mr-1 text-[#A2CB52]" />
                                        Data Encrypted
                                    </div>
                                    <div className="flex items-center text-xs text-gray-400">
                                        <Lock size={14} className="mr-1 text-[#A2CB52]" />
                                        Privacy Protected
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </FadeIn>
            </div>
            {/* Schedule Modal */}
            <ScheduleModal
                isOpen={isScheduleOpen}
                onClose={() => setIsScheduleOpen(false)}
            />
        </div>
    );
};

export default Contact;
